/**
 * OmniConnX Web Analytics Tracker
 *
 * Tracks: page views, registrations, logins, session info
 * Collects: device, browser, screen, language, timezone, referrer
 * Privacy:  no cookies, IP anonymised server-side, GDPR-friendly
 */
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '';

// Stable session ID for this browser tab (not persisted across tabs/sessions)
let _sessionId = sessionStorage.getItem('_ca_sid');
if (!_sessionId) {
  _sessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  sessionStorage.setItem('_ca_sid', _sessionId);
}

let _sessionStart = Date.now();

/** Common device / locale payload collected once */
const getClientInfo = () => ({
  screenWidth: window.screen.width,
  screenHeight: window.screen.height,
  language: navigator.language || navigator.userLanguage || null,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
});

/**
 * Low-level fire-and-forget tracker — never throws.
 * Uses the public /api/v1/analytics/track endpoint (no auth needed).
 */
const _send = async (eventType, extras = {}) => {
  try {
    // Include JWT token if logged in so backend populates userId on events
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    await axios.post(`${API_URL}/api/v1/analytics/track`, {
      sessionId: _sessionId,
      eventType,
      referrer: document.referrer || null,
      ...getClientInfo(),
      ...extras,
    }, { headers });
  } catch {
    // Silently ignore — analytics must never break the app
  }
};

/** Track a page view */
export const trackPageView = (path) => {
  _send('PAGE_VIEW', { page: path || window.location.pathname });
};

/** Track successful user registration */
export const trackRegistration = () => {
  _send('REGISTRATION', { page: window.location.pathname });
};

/** Track successful login */
export const trackLogin = () => {
  _send('LOGIN', { page: window.location.pathname });
};

/** Track logout */
export const trackLogout = () => {
  const duration = Math.round((Date.now() - _sessionStart) / 1000);
  _send('SESSION_END', { durationSeconds: duration, page: window.location.pathname });
  _send('LOGOUT', { page: window.location.pathname });
};

/** Track feature usage (e.g. "upload_content", "create_deal") */
export const trackFeature = (featureName) => {
  _send('FEATURE_USE', { page: featureName });
};

/** Track a button/link click */
export const trackClick = (elementName) => {
  _send('CLICK', { page: elementName || window.location.pathname });
};

/** Track a client-side error (message + optional full stack trace) */
export const trackError = (errorMessage, errorStack) => {
  const msg = errorMessage ? String(errorMessage) : 'Unknown error';
  _send('ERROR', {
    page: `${window.location.pathname}: ${msg}`,
    errorMessage: msg.slice(0, 2000),
    errorStack: errorStack ? String(errorStack).slice(0, 8000) : null,
  });
};

// ── Auto-track global errors ────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    // Prefer the real Error's stack; fall back to the source location the event carries.
    const stack = event.error?.stack
      || (event.filename ? `at ${event.filename}:${event.lineno || 0}:${event.colno || 0}` : null);
    trackError(event.message || 'Unknown error', stack);
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const msg = reason?.message || (typeof reason === 'string' ? reason : 'Unhandled promise rejection');
    trackError(msg, reason?.stack || null);
  });
}
